import React from "react";
//#region redux
import { useSelector } from "react-redux";
import { AppState } from "store";
//#endregion
//#region types
import { EntityIdParams } from "models/propTypes/urlParamsTypes";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { TranslationKeys } from "translations";
import { StyleEnums } from "models/enums";
import { InvoiceFormTypes } from "models/formTypes";
import { FormHandles } from "models/propTypes/genericFormProps";
import { IconDefinitionConstants, RouteConstants } from "utils/constants";
//#endregion
//#region components
import {
  InvoiceDetailsFormContainer,
  InvoiceFormContainer,
  InvoiceListView,
  RecurringDetails,
  RecurringInvoiceBanner,
  RecurringInvoiceFooter,
  RecurringInvoiceStateFlow,
  useInvoicePermissions,
} from "components/invoice";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { ErrorView } from "components/error";
import {
  EntityTitle,
  FormValidity,
  GenericCommentsView,
  LoadingWithLogo,
  PageLayouts,
  RouteBreadCrumb,
  Skeletons,
  StyledButton,
  ViewPanel,
} from "components/common";
import { Grid } from "@mui/material";
import { useRecurringInvoiceService } from "serviceHooks";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import { LocalizationHelperInstance, UrlHelper } from "utils/helpers";

const RecurringInvoiceView: React.FC = () => {
  const recurringInvoiceService = useRecurringInvoiceService();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(true);
  const [invoice, setInvoice] = React.useState<WintSuperkollAPIModel.RecurringInvoice>();
  const [key, setKey] = React.useState(0);
  const [displayType, setDisplayType] = React.useState<StyleEnums.DisplayType>(StyleEnums.DisplayType.Info);

  const formRef = React.useRef<FormHandles>(null);
  const noVatCompany = useSelector((store: AppState) => store.auth.selectedCompany.noVat);

  const { canUseRecurringInvoices } = useInvoicePermissions();

  const urlParams = useParams<EntityIdParams>();
  const loadInvoice = (invoiceId: number) => {
    recurringInvoiceService
      .get(invoiceId)
      .then((res) => {
        setInvoice(res);
        setKey(key + 1);
      })
      .finally(() => setLoading(false));
  };

  React.useEffect(() => {
    const invoiceId = urlParams.id;
    if (invoiceId) {
      if (invoice?.id !== Number.parseInt(invoiceId)) {
        setLoading(true);
        loadInvoice(Number.parseInt(invoiceId));
      }
    } else {
      setInvoice(undefined);
      setLoading(false);
    }
  }, [urlParams]);

  const reloadInvoice = () => {
    if (invoice) {
      loadInvoice(invoice.id);
    }
  };

  const onUpdateInvoice = () => {
    return (
      (formRef.current && formRef.current.submit(onInvoiceFormSubmit)) || new Promise<void>((resolve) => resolve())
    );
  };

  const filter: InvoiceFormTypes.Filter = React.useMemo(() => ({ recurringInvoiceId: invoice?.id }), [invoice?.id]);

  React.useEffect(() => {
    const updatedDisplayType = getUIDisplayState();
    if (updatedDisplayType !== displayType) setDisplayType(updatedDisplayType);
  }, [invoice?.status]);

  const getUIDisplayState = () => {
    switch (invoice?.status) {
      case WintSuperkollAPIModel.RecurringInvoiceState.Ongoing:
        return StyleEnums.DisplayType.Info;
      default:
        return StyleEnums.DisplayType.None;
    }
  };

  const editInvoice = () => {
    navigate(UrlHelper.formatPath(`/Invoice/Create?${RouteConstants.QueryParamStrings.Recurring}=${invoice?.id}`), {
      state: { background: location },
    });
  };

  const onInvoiceFormSubmit = React.useCallback((form: InvoiceFormTypes.Create) => {
    const invoicePost = recurringInvoiceService.convertFormToAPIRequest(form, noVatCompany);
    return recurringInvoiceService.createOrUpdateInvoice(form.id, invoicePost);
  }, []);

  return (
    <PageLayouts.EntityFullScreen
      id={invoice?.id ?? 0}
      entityKey={`invoice`}
      navigation={
        <RouteBreadCrumb
          current={loading ? "" : invoice?.customerName ?? TranslationKeys.Invoice.CreateInvoice}
          showHome
        />
      }
      hasPermissions={canUseRecurringInvoices}
    >
      {loading ? (
        <LoadingWithLogo />
      ) : invoice ? (
        <FormValidity
          render={(isValid, onValidityChange) => (
            <PageLayouts.EntityViewLayout
              displayType={displayType}
              leftColumnSize="sm"
              upperLeftComponent={
                <EntityTitle
                  status={
                    invoice.status === WintSuperkollAPIModel.RecurringInvoiceState.Created
                      ? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.Draft)
                      : invoice.status === WintSuperkollAPIModel.RecurringInvoiceState.Ongoing
                      ? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Active)
                      : LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.InActive)
                  }
                  subTitle={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.RecurringInvoice)}
                  title={invoice.customerName ?? ""}
                />
              }
              upperRightComponent={
                invoice ? (
                  <RecurringInvoiceStateFlow key={key} invoiceId={invoice.id} />
                ) : (
                  <Skeletons.StateFlowSkeleton key="steps-skeleton" stepCount={4} />
                )
              }
              lowerLeftComponent={
                <Grid container item>
                  <RecurringDetails invoice={invoice} displayType={displayType} />
                </Grid>
              }
              footer={
                <RecurringInvoiceFooter
                  isValid={isValid}
                  invoice={invoice}
                  createOrUpdateInvoice={onUpdateInvoice}
                  reloadHandler={reloadInvoice}
                  markAsDoneHandler={reloadInvoice}
                />
              }
            >
              <>
                <RecurringInvoiceBanner invoice={invoice} loadInvoice={reloadInvoice} />

                <ViewPanel
                  title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.LatestInvoices)}
                  icon={IconDefinitionConstants.WSend}
                >
                  <InvoiceListView filter={filter} includeSummary={false} />
                </ViewPanel>
                <ViewPanel
                  title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.RecurringSettings)}
                  icon={IconDefinitionConstants.WSettings}
                >
                  <InvoiceFormContainer
                    ref={formRef}
                    invoice={invoice}
                    onFormValidationChange={onValidityChange}
                    reloadInvoice={reloadInvoice}
                    isRecurring
                    showOnlyRecurringFields
                  />
                </ViewPanel>

                <ViewPanel
                  title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.InvoiceDetails)}
                  icon={IconDefinitionConstants.WEdit}
                  classes={{ content: cssExports.detailsFormContainer }}
                  topRightContent={
                    invoice?.status !== WintSuperkollAPIModel.RecurringInvoiceState.Stopped ? (
                      <StyledButton size="sm" onClick={editInvoice}>
                        {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Edit)}
                      </StyledButton>
                    ) : (
                      <></>
                    )
                  }
                >
                  <InvoiceDetailsFormContainer
                    invoice={invoice}
                    loadInvoice={reloadInvoice}
                    isRecurring
                    attachments={[]}
                  />
                </ViewPanel>
                <GenericCommentsView
                  parentId={invoice.id}
                  type={WintSuperkollAPIModel.GenericCommentType.RecurringInvoice}
                />
              </>
            </PageLayouts.EntityViewLayout>
          )}
        />
      ) : (
        <ErrorView />
      )}
    </PageLayouts.EntityFullScreen>
  );
};

export default RecurringInvoiceView;
